import { z } from 'zod';
import { CommonConditionOperatorSchema } from './common-condition-operators.js';
import { deletedFilterFields, refineDeletedFilterMutualExclusion } from './common.js';
// ─── Action schemas ──────────────────────────────────────────────────────
// Each variant is a discriminated union member on `type`. Keep these in sync
// with packages/shared/src/types/file-workflow.ts and the executor adapters
// in apps/api/src/workers/file-workflow/.
const TagRefSchema = z.union([
    z.object({ id: z.string().min(1).max(26) }),
    z.object({ name: z.string().min(1).max(120) }),
]);
const ContainerRefSchema = z.union([
    z.object({ id: z.string().min(1).max(26) }),
    z.object({ slug: z.string().min(1).max(64) }),
]);
export const ActionAssignTagSchema = z.object({
    type: z.literal('assign_tag'),
    tag: TagRefSchema,
});
/**
 * Adds the document to the named storage container.
 *
 * **Yield-if-already-contained (CTSK-45, 0.1.76)**: at execution
 * time this action is a no-op if the document already has ANY
 * container assignment. See `ActionAssignContainer` in
 * `packages/shared/src/types/file-workflow.ts` for the full behavior
 * note. The schema shape is unchanged — no opt-out field today; a
 * future FR could add `unless_already_in_container: false` for
 * workflows that legitimately need multi-container semantics.
 */
export const ActionAssignContainerSchema = z.object({
    type: z.literal('assign_container'),
    container: ContainerRefSchema,
});
export const ActionSetFrontmatterSchema = z.object({
    type: z.literal('set_frontmatter'),
    fields: z.record(z.string(), z.string()).refine((m) => Object.keys(m).length > 0, { message: 'set_frontmatter requires at least one field' }),
});
export const ActionRenameFileSchema = z.object({
    type: z.literal('rename_file'),
    template: z.string().min(1).max(500),
    alsoUpdateSlug: z.boolean().optional(),
});
export const ActionCheckLinksSchema = z.object({
    type: z.literal('check_links'),
});
export const ActionLinkToParentSchema = z.object({
    type: z.literal('link_to_parent'),
    suffix: z.string().min(1).max(64).optional(),
});
/** 0.1.17 — project values from sidecar.frontmatter into
 *  documents.customProperties for every CP scoped to this doc that
 *  carries a source pointer. No parameters (the per-CP source path
 *  drives the projection). Idempotent: re-running on a doc whose
 *  frontmatter and CP definitions are unchanged is a no-op. */
export const ActionProjectToCustomPropertiesSchema = z.object({
    type: z.literal('project_to_custom_properties'),
});
/**
 * 0.1.5X — CTSK-166 Phase A3 — External Clients Nest approval action.
 *
 * Transitions an `access/pending` document in the External Clients Nest
 * to `access/approved` by minting a per-client PAT and back-updating
 * the doc's tags + CPs atomically. Invoked via the tool palette on
 * the pending document.
 *
 * Preconditions (executor enforces before mutation):
 *   - Doc carries `access/pending` and NOT `access/approved` (COO P4 mutex)
 *   - CP `source_tag` non-empty
 *   - CP `requested_capabilities` non-empty
 *   - Actor holds the `pats:mint_from_approval` capability
 *   - intersection(requested_capabilities, actor.capabilities) NON-EMPTY (COO P10)
 *   - No other approved doc in the org already carries the same linked_pat_id
 *     (COO P3 — DB-enforced via migration 0036 partial unique index)
 *
 * On success (single transaction):
 *   - Mint PAT with capabilities = intersection, source_tag = CP source_tag,
 *     name derived from `mintPolicy.namePattern`, expires_in_days from
 *     `mintPolicy.expiresInDays`
 *   - Add tag `access/approved`; remove tag `access/pending` (P4 swap)
 *   - Set CPs: linked_pat_id, approved_at, approved_by_user_id (from actor)
 *   - Emit tag-audit rows (patched-through per 0.1.16 tag-audit design)
 *   - Return the one-time PAT token in the action's returns envelope
 *
 * The bundle-side JSON representation is verbose (mintPolicy + preconditions
 * + onSuccess blocks) so operators can read it. The executor collapses this
 * into the actual sequence; the JSON is a contract, not free-form config.
 */
const ApproveAndMintPatMintPolicySchema = z.object({
    capabilitiesFrom: z.literal('custom_property:requested_capabilities'),
    capabilityIntersection: z.literal('actor'),
    sourceTagFrom: z.literal('custom_property:source_tag'),
    expiresInDays: z.number().int().min(1).max(3650),
    /** CTSK-296 (0.1.114+): operator-set ceiling for the requester's
     *  `requested_expires_in_days` TTL preference. When the requested
     *  value exceeds this, the mint clamps to it and reports
     *  `expiryClampedFrom`. Optional — readers default to 30 when
     *  absent, so bundles installed before CTSK-296 need no migration.
     *  Only caps the REQUESTER's preference; the role default
     *  (`expiresInDays`) and an explicit operator override at approve
     *  time are not clamped. */
    maxExpiresInDays: z.number().int().min(1).max(3650).optional(),
    namePattern: z.string().min(1).max(120),
});
const ApproveAndMintPatPreconditionSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('tag'),
        operator: z.enum(['present', 'not_present']),
        value: z.string().min(1).max(120),
    }),
    z.object({
        field: z.literal('custom_property'),
        key: z.string().min(1).max(64),
        operator: z.enum(['not_empty', 'empty']),
    }),
]);
const ApproveAndMintPatOnSuccessSchema = z.object({
    addTags: z.array(z.string().min(1).max(120)).default([]),
    removeTags: z.array(z.string().min(1).max(120)).default([]),
    setCustomProperties: z.record(z.string(), z.string()),
});
export const ActionApproveAndMintPatSchema = z.object({
    type: z.literal('approve_and_mint_pat'),
    requiresCapability: z.literal('pats:mint_from_approval'),
    preconditions: z.array(ApproveAndMintPatPreconditionSchema).min(1).max(20),
    mintPolicy: ApproveAndMintPatMintPolicySchema,
    onSuccess: ApproveAndMintPatOnSuccessSchema,
});
/**
 * 0.1.55 — CTSK-166 Phase D — deny_access_request.
 *
 * Rejection counterpart to approve_and_mint_pat. Same shape as the
 * approve action's precondition + onSuccess halves, but without the
 * mintPolicy (no PAT is minted; the request just gets marked
 * denied). Preconditions verify the doc is in a state where denial
 * is meaningful — `access/pending` present, `access/denied` NOT
 * present (P4 tag mutex).
 *
 * The route handler (POST /documents/:id/actions/deny-access-request)
 * carries the denial_reason in the request body; it doesn't come from
 * the workflow action config. The onSuccess block still lets an
 * operator pre-declare what CPs to stamp (denied_at, denied_by_user_id)
 * in a portable way — the route handler injects the actual denial_reason
 * value at fire time.
 */
const DenyAccessRequestPreconditionSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('tag'),
        operator: z.enum(['present', 'not_present']),
        value: z.string().min(1).max(120),
    }),
    z.object({
        field: z.literal('custom_property'),
        key: z.string().min(1).max(64),
        operator: z.enum(['not_empty', 'empty']),
    }),
]);
const DenyAccessRequestOnSuccessSchema = z.object({
    addTags: z.array(z.string().min(1).max(120)).default([]),
    removeTags: z.array(z.string().min(1).max(120)).default([]),
    setCustomProperties: z.record(z.string(), z.string()),
});
export const ActionDenyAccessRequestSchema = z.object({
    type: z.literal('deny_access_request'),
    requiresCapability: z.literal('pats:deny_access_request'),
    preconditions: z.array(DenyAccessRequestPreconditionSchema).min(1).max(20),
    onSuccess: DenyAccessRequestOnSuccessSchema,
});
/**
 * 0.1.5X — CTSK-170 Part A/4 — change_client_role.
 *
 * Rotates an already-approved client's PAT to a new role. The new
 * role handle is supplied in the route request body (not the workflow
 * action config), analogously to how deny_access_request carries
 * denialReason in the body. The adapter resolves the handle against
 * ROLE_CATALOG (shared constant), intersects the target caps with the
 * approver's live capabilities (COO P10), mints a new PAT with the
 * intersected set + same sourceTag, and hard-revokes the old PAT.
 * Grace-period soft-revoke is a follow-up FR — MVP is hard-revoke to
 * keep the security model conservative.
 *
 * Preconditions verify the doc is in a state where role changes are
 * meaningful — `access/approved` present (must already be approved),
 * `access/revoked` NOT present (can't rotate a revoked client's PAT).
 *
 * onSuccess templates support all approve/deny substitutions
 * (`{{now}}`, `{{actor.id}}`) PLUS role-specific:
 *   {{newRoleHandle}}   → the target role's handle (e.g. "doc-writer")
 *   {{newRoleVersion}}  → the target role's version string
 *   {{oldPatId}}         → the id of the PAT being revoked
 *   {{newPatId}}         → the id of the newly-minted PAT
 *
 * The `previous_pat_ids` CP is a JSON-encoded array appended to on
 * each rotation — the adapter reads the current value, splices the
 * old PAT id in, writes back. Because the CP is text-typed (no array
 * primitive in the CP allowlist — see feedback_ingest_is_multipart /
 * captain-tasko-bundle-authoring-traps memories), this uses the same
 * JSON-encoded-array pattern as `requested_capabilities`.
 */
const ChangeClientRolePreconditionSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('tag'),
        operator: z.enum(['present', 'not_present']),
        value: z.string().min(1).max(120),
    }),
    z.object({
        field: z.literal('custom_property'),
        key: z.string().min(1).max(64),
        operator: z.enum(['not_empty', 'empty']),
    }),
]);
const ChangeClientRoleOnSuccessSchema = z.object({
    addTags: z.array(z.string().min(1).max(120)).default([]),
    removeTags: z.array(z.string().min(1).max(120)).default([]),
    setCustomProperties: z.record(z.string(), z.string()),
});
const ChangeClientRoleMintPolicySchema = z.object({
    /** Source of the new role's capability set. `role:handle` means the
     *  adapter looks up ctx.newRoleHandle in ROLE_CATALOG at fire time. */
    capabilitiesFrom: z.literal('role:handle'),
    /** Always 'actor' — the intersection guard is non-negotiable per
     *  COO P10. Kept as a literal so a future policy tweak requires an
     *  explicit schema change. */
    capabilityIntersection: z.literal('actor'),
    /** Same sourceTag as the pre-rotation PAT (preserves audit
     *  attribution across the role change). Only literal supported. */
    sourceTagFrom: z.literal('custom_property:source_tag'),
    /** Standard expiry — same range as approve_and_mint_pat. */
    expiresInDays: z.number().int().min(1).max(3650),
    /** Name template. Substitutions:
     *    {source_tag}  — sourceTag from the doc
     *    {role}        — the target role handle */
    namePattern: z.string().min(1).max(120),
});
export const ActionChangeClientRoleSchema = z.object({
    type: z.literal('change_client_role'),
    requiresCapability: z.literal('pats:change_client_role'),
    preconditions: z.array(ChangeClientRolePreconditionSchema).min(1).max(20),
    mintPolicy: ChangeClientRoleMintPolicySchema,
    onSuccess: ChangeClientRoleOnSuccessSchema,
});
/**
 * 0.1.57 — CTSK-181 — revoke_client_pat.
 *
 * Terminal counterpart to approve_and_mint_pat. Sets `revoked_at` on
 * the doc's linked PAT and swaps the doc from `access/approved` to
 * `access/revoked`. Structural mirror of `deny_access_request` (same
 * precondition shape, same onSuccess-driven tag swap + CP set) with
 * one additional side-effect: the SQL UPDATE against
 * personal_access_tokens.revoked_at inside the same transaction.
 *
 * The revocation_reason string is supplied in the route body
 * (analogous to deny's denialReason). onSuccess templates support
 * `{{revocationReason}}`, `{{now}}`, `{{actor.id}}`,
 * `{{actor.displayName}}` for CP substitution.
 *
 * Preconditions verify the doc is in a state where revocation is
 * meaningful — `access/approved` present (must already be approved),
 * `access/revoked` NOT present (can't revoke twice).
 */
const RevokeClientPatPreconditionSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('tag'),
        operator: z.enum(['present', 'not_present']),
        value: z.string().min(1).max(120),
    }),
    z.object({
        field: z.literal('custom_property'),
        key: z.string().min(1).max(64),
        operator: z.enum(['not_empty', 'empty']),
    }),
]);
const RevokeClientPatOnSuccessSchema = z.object({
    addTags: z.array(z.string().min(1).max(120)).default([]),
    removeTags: z.array(z.string().min(1).max(120)).default([]),
    setCustomProperties: z.record(z.string(), z.string()),
});
export const ActionRevokeClientPatSchema = z.object({
    type: z.literal('revoke_client_pat'),
    requiresCapability: z.literal('pats:revoke_client_pat'),
    preconditions: z.array(RevokeClientPatPreconditionSchema).min(1).max(20),
    onSuccess: RevokeClientPatOnSuccessSchema,
});
export const FileWorkflowActionSchema = z.discriminatedUnion('type', [
    ActionAssignTagSchema,
    ActionAssignContainerSchema,
    ActionSetFrontmatterSchema,
    ActionRenameFileSchema,
    ActionCheckLinksSchema,
    ActionLinkToParentSchema,
    ActionProjectToCustomPropertiesSchema,
    ActionApproveAndMintPatSchema,
    ActionDenyAccessRequestSchema,
    ActionChangeClientRoleSchema,
    ActionRevokeClientPatSchema,
]);
// ─── Condition schemas (CTSK-437) ────────────────────────────────────────
// Mirrors the tagging-rule DSL shape (tagging-rule.ts) with an extended
// operator vocabulary: every positive operator gets a negated pair, so
// producer-owned artifacts can opt OUT declaratively (e.g. skip a rename
// workflow when the doc carries any `source/*` tag).
export const FileWorkflowConditionFieldSchema = z.enum([
    'tag',
    'metadata_field',
    'custom_property',
    'file_type',
    'filename',
    'container',
]);
// CTSK-442 — the operator vocabulary is now the shared
// CommonConditionOperatorSchema (packages/shared/src/schemas/
// common-condition-operators.ts), consumed by BOTH the file-workflow and
// tagging-rule engines. Re-exported under the original name for backward
// compat — same 14 operators, same universal-negation semantics.
export const FileWorkflowConditionOperatorSchema = CommonConditionOperatorSchema;
/**
 * Condition on a file-workflow. Evaluated after `filenamePattern` match,
 * before any action executes.
 *
 * VISIBILITY (sharp edge): file-workflows fire `on_upload`, BEFORE
 * tagging rules run. So `field: 'tag'` conditions see ONLY tags stamped
 * by the ingest metadata payload (e.g. RG's `source/report-generator`).
 * Tags derived by later-running tagging rules will NOT be visible here.
 *
 * `field: 'metadata_field'` reads sidecar frontmatter keys (parsed at
 * ingest). `field: 'custom_property'` reads CPs projected at ingest.
 *
 * `fieldKey` is required for `metadata_field` and `custom_property`
 * (the frontmatter key or CP key name). `value` is required except
 * for `is_empty` / `is_not_empty`.
 */
export const FileWorkflowConditionSchema = z.object({
    field: FileWorkflowConditionFieldSchema,
    fieldKey: z.string().min(1).max(200).optional(),
    operator: FileWorkflowConditionOperatorSchema,
    value: z.string().max(2000).optional(),
}).superRefine((cond, ctx) => {
    if ((cond.field === 'metadata_field' || cond.field === 'custom_property') && !cond.fieldKey) {
        ctx.addIssue({
            code: 'custom',
            path: ['fieldKey'],
            message: `fieldKey is required when field is '${cond.field}'`,
        });
    }
    const noValueOps = new Set(['is_empty', 'is_not_empty']);
    if (!noValueOps.has(cond.operator) && !cond.value) {
        ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: `value is required for operator '${cond.operator}'`,
        });
    }
});
// ─── Workflow schemas ────────────────────────────────────────────────────
// 0.1.5X — 'manual' added for CTSK-166 Phase A3 approve_and_mint_pat
// invocation from the tool palette on a document.
export const FileWorkflowTriggerSchema = z.enum(['on_upload', 'manual']);
/** Validates filenamePattern is a compilable regex. */
const FilenamePatternSchema = z.string().min(1).max(500).refine((s) => {
    try {
        new RegExp(s);
        return true;
    }
    catch {
        return false;
    }
}, { message: 'filenamePattern must be a valid regular expression' });
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const FileWorkflowCreateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    priority: z.number().int().min(0).max(1000).default(10),
    enabled: z.boolean().default(true),
    trigger: FileWorkflowTriggerSchema.default('on_upload'),
    filenamePattern: FilenamePatternSchema.nullable().optional(),
    // CTSK-437 — optional conditions[] gate between filenamePattern match and
    // the action loop. Omitted / empty = current behavior (match everything
    // post-filenamePattern). Backward compatible: existing bundles unchanged.
    matchLogic: z.enum(['all', 'any']).default('all'),
    conditions: z.array(FileWorkflowConditionSchema).max(20).optional(),
    actions: z.array(FileWorkflowActionSchema).min(1).max(50),
}).strict();
// .strip() restores Zod's default unknown-key tolerance for PATCH —
// CTSK-445 made Create strict, and .partial() would otherwise inherit
// `unknownKeys: "strict"`. Patch semantics deliberately unchanged.
export const FileWorkflowPatchSchema = FileWorkflowCreateSchema.partial().strip();
// CTSK-520 — `GET /file-workflows` list query: the shared soft-delete
// filters (live-only by default; includeDeleted / deletedOnly need
// admin:view_deleted, enforced in the route like custom-properties).
export const FileWorkflowsListQuerySchema = z
    .object({
    ...deletedFilterFields,
})
    .superRefine(refineDeletedFilterMutualExclusion);
//# sourceMappingURL=file-workflow.js.map